import { formatLength } from "@evergis/react";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { DragEvent, sGisMouseMoveEvent } from "@evergis/sgis/es/commonEvents";
import { Circle } from "@evergis/sgis/es/controls/Circle";
import { PolyDragParams } from "@evergis/sgis/es/controls/PolyDrag";
import { LabelFeature } from "@evergis/sgis/es/features/Label";
import { length as calcLength } from "@evergis/sgis/es/geotools";
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { Map as SMap } from "@evergis/sgis/es/Map";
import { DEFAULT_LABEL_POSITION } from "../../utils";
import { textSymbol } from "../TooltipSymbol";

export type BaseCircleCreatorProps = PolyDragParams & {
  showRadiusLabel?: boolean;
};

const LABEL_Y_OFFSET = -5;

export class BaseCircleCreator extends Circle {
  private readonly painter: DomPainter;

  private tempLabel: LabelFeature;

  private readonly showRadiusLabel: boolean;

  constructor(map: SMap, painter: DomPainter, params: BaseCircleCreatorProps) {
    const { showRadiusLabel, ...circleParams } = params;

    super(map, circleParams);

    this.painter = painter;

    this.showRadiusLabel = !!showRadiusLabel;

    if (showRadiusLabel) {
      this.tempLabel = new LabelFeature(DEFAULT_LABEL_POSITION, {
        crs: map.crs,
        symbol: textSymbol,
      });
    }
  }

  protected _activate() {
    super._activate();
    this.setCursor("pointer");

    this.addLabel();
  }

  protected _deactivate() {
    super._deactivate();
    this.setCursor("default");

    this.removeLabel();
  }

  protected updateDrawing(event: DragEvent | sGisMouseMoveEvent) {
    super.updateDrawing(event);

    if (this.showRadiusLabel && this._activeFeature) {
      const feature = this._activeFeature;

      const { crs, bbox, centroid } = feature;

      const radiusCoordinates = [
        [
          [centroid[0], centroid[1]],
          [bbox.xMax, centroid[1]],
        ],
      ];
      const length = calcLength(radiusCoordinates as Coordinates[][], crs);

      this.tempLabel.content = formatLength(length);
      this.tempLabel.position = [
        bbox.xMax - (bbox.xMax - centroid[0]) / 2,
        centroid[1] - LABEL_Y_OFFSET * this.map.resolution,
      ];
      this._tempLayer.redraw();
    }
  }

  private addLabel() {
    if (this.tempLabel && !this._tempLayer.has(this.tempLabel)) {
      this._tempLayer.add(this.tempLabel);
    }
  }

  private removeLabel() {
    if (this.tempLabel && this._tempLayer.has(this.tempLabel)) {
      this._tempLayer.remove(this.tempLabel);
      delete this.tempLabel;
    }
  }

  private setCursor(cursor: "pointer" | "default") {
    const { wrapper } = this.painter;

    if (wrapper && typeof wrapper !== "string") {
      wrapper.style.cursor = cursor;
    }
  }
}
